import React from "react";

import styled from "@emotion/styled";

import FlexContainer from "shared/components/FlexContainer";

import { COLORS, MEDIA_QUERIES, colors } from "shared/config/constants";

import * as icons from "./icons";
import { getIcon, HoverTracker, withHover } from "./icons.utils";

const BellIcon = withHover(icons.Bell, icons.BellHover);
const ProfileIcon = withHover(icons.Profile, icons.ProfileHover);
const SupportIcon = withHover(icons.Support, icons.SupportHover);
const SettingsIcon = withHover(
  icons.SidebarSettings,
  icons.SidebarSettingsHover
);
const EmployeesIcon = withHover(icons.Employees, icons.EmployeesHover);
const MyPropertyBoldIcon = icons.MyPropertyHover;
const MyPropertyIcon = withHover(
  icons.SidebarInventory,
  icons.SidebarInventoryHover
);
const DealsIcon = withHover(
  icons.SidebarApplications,
  icons.SidebarApplicationsHover
);
const LeadsIcon = withHover(icons.SidebarLeads, icons.SidebarLeadsHover);
const ApplicantsTenantsIcon = withHover(
  icons.ApplicantsTenants,
  icons.ApplicantsTenantsHover
);
const ApplicantInvite = getIcon(
  icons.ApplicantInviteBase,
  icons.ApplicantInviteHover,
  icons.ApplicantInviteDisabled
);
ApplicantInvite.tags = [
  "applicant",
  "invite",
  "plus",
  "hover",
  "disabled",
  "full",
  "complete",
];

const StarOutlineIcon = withHover(
  icons.BaseStarOutlineIcon,
  icons.StarOutlineHoverIcon
);
const DropdownIcon = withHover(icons.BaseDropdownIcon, icons.DropdownHoverIcon);

const DeleteIconWithHover = withHover(
  icons.BaseDeleteIcon,
  icons.DeleteHoverIcon
);
const DeleteIcon = (props) => (
  <HoverTracker Icon={DeleteIconWithHover} {...props} />
);
const DeleteSmallIcon = withHover(
  icons.BaseDeleteSmallIcon,
  icons.DeleteSmallHoverIcon
);
const ImportantIcon = withHover(icons.Important, icons.ImportantHover);
const SignOutIcon = withHover(icons.SignOut, icons.SignOutHover);
const PersonalInfoIcon = withHover(icons.PersonalInfo, icons.PersonalInfoHover);
const ShowPasswordIcon = withHover(icons.ShowPassword, icons.ShowPasswordHover);
const HidePasswordIcon = withHover(icons.HidePassword, icons.HidePasswordHover);
const ApplicationsIcon = withHover(icons.Applications, icons.ApplicationsHover);
const LeaseEyeIcon = withHover(icons.LeaseEye, icons.LeaseEye);
const DocumentsIcon = withHover(icons.Documents, icons.Documents);
const ArrowLeftUpIcon = getIcon(icons.LeftUp, icons.LeftOver);
const ArrowRightUpIcon = getIcon(icons.RightUp, icons.RightOver);
const ZoomPlusIcon = getIcon(
  icons.ZoomPlus,
  icons.ZoomPlusHover,
  icons.ZoomPlusDisabled
);
const ZoomMinusIcon = getIcon(
  icons.ZoomMinus,
  icons.ZoomMinusHover,
  icons.ZoomMinusDisabled
);
const CrossRemoveIcon = getIcon(icons.CrossRemove, icons.CrossRemoveHovered);
const IntegrationsMenuItemIcon = withHover(
  icons.SidebarMarketplace,
  icons.SidebarMarketplaceHover
);
const SelectArrowCompleteIcon = getIcon(
  icons.SelectArrowIcon,
  icons.SelectArrowIcon,
  icons.SelectArrowIconDisabled
);
const DollarSignIcon = getIcon(
  icons.DollarSignGreen,
  icons.DollarSignGreen,
  icons.DollarSignGray
);

const NoteIcon = getIcon(
  icons.NoteActiveIcon,
  icons.NoteHoverIcon,
  icons.NoteDisabledIcon
);

const DownloadPdfDoc = getIcon(
  icons.DownloadPdfDocBase,
  icons.DownloadPdfDocHover
);

DownloadPdfDoc.tags = ["pdf", "download"];

const DownloadPdfDocGray = getIcon(
  styled(icons.DownloadPdfDocBase)`
    path {
      fill: ${COLORS.gray3};
    }
  `,
  icons.DownloadPdfDocBase
);

DownloadPdfDocGray.tags = ["pdf", "download", "gray"];

const BaseDeleteSmallNoCircleDisabledIcon = styled(
  icons.BaseDeleteSmallNoCircleIcon
)({
  opacity: 0.3,
});
const DeleteSmallNoCircleIcon = getIcon(
  icons.BaseDeleteSmallNoCircleIcon,
  icons.DeleteSmallHoverIcon,
  BaseDeleteSmallNoCircleDisabledIcon
);

const ArrowUpIcon = () => (
  <icons.ArrowDownIcon style={{ transform: "rotate(180deg)" }} />
);

// eslint-disable-next-line react/prop-types
const ChevronUpIcon = ({ style, ...props }) => (
  <icons.ChevronDownIcon
    style={{ transform: "rotate(180deg)", ...style }}
    {...props}
  />
);

// eslint-disable-next-line react/prop-types
const ChevronUpNewIcon = ({ style, ...props }) => (
  <icons.ChevronDownNewIcon
    style={{ transform: "rotate(180deg)", ...style }}
    {...props}
  />
);

// eslint-disable-next-line react/prop-types
const ChevronUpGreenIcon = ({ style, ...props }) => (
  <icons.ChevronDownGreenIcon
    style={{ transform: "rotate(180deg)", ...style }}
    {...props}
  />
);

// eslint-disable-next-line react/prop-types
const ChevronUpWhiteBoldIcon = ({ style, ...props }) => (
  <icons.ChevronDownWhiteBoldIcon
    style={{ transform: "rotate(180deg)", ...style }}
    {...props}
  />
);

// eslint-disable-next-line react/prop-types
const ChevronBoldUpIcon = ({ style, ...props }) => (
  <icons.ChevronBoldDownIcon
    style={{ transform: "rotate(180deg)", ...style }}
    {...props}
  />
);

const DropdownButtonChevronUpIcon = (props) => (
  <icons.DropdownButtonChevronDownIcon
    {...props}
    style={{ transform: "rotate(180deg)" }}
  />
);

const ProfileContainer = styled(FlexContainer)`
  width: 56px;
  height: 40px;
  padding: 4px;
  border-radius: 4px;
  background: transparent;
  &:hover {
    background: ${colors.neutral[200]};
  }
`;

// TODO: rename for consistency
// eslint-disable-next-line react/prop-types
const ProfileIconWithChevron = ({ active = false, hovered, ...props }) => (
  <ProfileContainer alignItems="center">
    <icons.Profile {...props} hovered={hovered} />
    {active ? (
      <ChevronUpNewIcon style={{ marginLeft: "3px" }} width={16} height={16} />
    ) : (
      <icons.ChevronDownNewIcon
        style={{ marginLeft: "3px", width: "16px", height: "16px" }}
      />
    )}
  </ProfileContainer>
);

const PdfMobileDownloadIcon = styled(
  withHover(icons.PdfDownloadMobile, icons.PdfDownloadMobileHover)
)({
  [MEDIA_QUERIES.w.gteMd]: {
    display: "none",
  },
});

const PdfDesktopDownloadIcon = styled(
  withHover(icons.PdfDownloadDesktop, icons.PdfDownloadDesktopHover)
)({
  [MEDIA_QUERIES.w.ltMd]: {
    display: "none",
  },
});

const PdfDownloadIcon = (props) => (
  <>
    <PdfMobileDownloadIcon {...props} />
    <PdfDesktopDownloadIcon {...props} />
  </>
);

const VerifiedIncomeAssetsDesktop = styled(icons.OperationSuccess)({
  [MEDIA_QUERIES.w.ltMd]: {
    display: "none",
  },
});
const VerifiedIncomeAssetsMobile = styled(icons.VerifiedIncomeAssetsSmall)({
  [MEDIA_QUERIES.w.gteMd]: {
    display: "none",
  },
});
const VerifiedIncomeAssetsIcon = (props) => (
  <>
    <VerifiedIncomeAssetsDesktop {...props} />
    <VerifiedIncomeAssetsMobile {...props} />
  </>
);

const NotVerifiedIncomeAssetsDesktop = styled(icons.NotVerifiedIncomeAssets)({
  [MEDIA_QUERIES.w.ltMd]: {
    display: "none",
  },
});
const NotVerifiedIncomeAssetsMobile = styled(
  icons.NotVerifiedIncomeAssetsSmall
)({
  [MEDIA_QUERIES.w.gteMd]: {
    display: "none",
  },
});
const NotVerifiedIncomeAssetsIcon = (props) => (
  <>
    <NotVerifiedIncomeAssetsDesktop {...props} />
    <NotVerifiedIncomeAssetsMobile {...props} />
  </>
);

const QRCodeGreenHoverIcon = withHover(icons.QRCodeIcon, icons.QRCodeGreenIcon);
QRCodeGreenHoverIcon.tags = ["QR", "download", "green", "hover"];

const DocumentsEmptyGreenHoverIcon = withHover(
  icons.DocumentsEmptyIcon,
  icons.DocumentsEmptyGreenIcon
);

DocumentsEmptyGreenHoverIcon.tags = ["documents", "copy", "green", "hover"];

const ExportIconHover = styled(
  withHover(icons.ExportIcon, icons.ExportIconGreen)
)({
  cursor: "pointer",
});

ExportIconHover.tags = ["export", "green", "hover"];

const OrangeExclamationSmall = (props) => (
  <icons.OrangeExclamation height="16px" width="16px" {...props} />
);
OrangeExclamationSmall.tags = ["exclamation", "warning", "orange", "small"];

// Bulb icons
const BulbGreenBig = () => <icons.BulbGreenSmall height="32px" width="32px" />;
const BulbPurpleBig = () => (
  <icons.BulbPurpleSmall height="32px" width="32px" />
);
const BulbOrangeBig = () => (
  <icons.BulbOrangeSmall height="32px" width="32px" />
);
const BulbRedBig = () => <icons.BulbRedSmall height="32px" width="32px" />;

BulbGreenBig.tags = ["lumen", "big", "bulb", "green"];
BulbOrangeBig.tags = ["lumen", "big", "bulb", "orange"];
BulbPurpleBig.tags = ["lumen", "big", "bulb", "purple"];
BulbRedBig.tags = ["lumen", "big", "bulb", "red"];

const AddAttachmentPlus = getIcon(
  icons.AddAttachmentPlusBase,
  icons.AddAttachmentPlusHover,
  icons.AddAttachmentPlusDisabled
);

AddAttachmentPlus.tags = ["attachment", "plus", "circle"];

const RefreshIconSmall = () => <icons.RefreshIcon height="15px" width="15px" />;
const RefreshRotateIconSmall = () => (
  <icons.RefreshRotateIcon height="15px" width="15px" />
);
RefreshIconSmall.tags = ["refresh", "small", "green"];
RefreshRotateIconSmall.tags = [
  "refresh",
  "rotate",
  "loading",
  "small",
  "green",
];

const ErrorXWarningGray = styled(icons.ErrorDarkXwarning)`
  path {
    fill: ${COLORS.gray3};
  }
`;
ErrorXWarningGray.tags = ["error", "warning", "gray"];

const ShowPasswordActionIcon = (props) => (
  <HoverTracker Icon={ShowPasswordIcon} {...props} />
);

const HidePasswordActionIcon = (props) => (
  <HoverTracker Icon={HidePasswordIcon} {...props} />
);

const LynkSuccessSmall = () => (
  <icons.BaseLynkSuccess height="24px" width="24px" />
);
const LynkSkippedSmall = () => (
  <icons.BaseLynkSkipped height="24px" width="24px" />
);

const SnapPassedSmall = () => (
  <icons.BaseSnapPassed height="24px" width="28px" />
);
const SnapFailedSmall = () => (
  <icons.BaseSnapFailed height="24px" width="28px" />
);
const SnapPendingSmall = () => (
  <icons.BaseSnapPending height="24px" width="28px" />
);

/* eslint-disable react/prop-types */
const LynkSuccess = ({ small }) =>
  small ? <LynkSuccessSmall /> : <icons.BaseLynkSuccess />;
const LynkSkipped = ({ small }) =>
  small ? <LynkSkippedSmall /> : <icons.BaseLynkSkipped />;

const SnapPassed = ({ small }) =>
  small ? <SnapPassedSmall /> : <icons.BaseSnapPassed />;
const SnapFailed = ({ small }) =>
  small ? <SnapFailedSmall /> : <icons.BaseSnapFailed />;
const SnapPending = ({ small }) =>
  small ? <SnapPendingSmall /> : <icons.BaseSnapPending />;
/* eslint-enable react/prop-types */

LynkSuccess.tags = [...icons.BaseLynkSuccess.tags, "dynamic", "small", "large"];
LynkSkipped.tags = [...icons.BaseLynkSkipped.tags, "dynamic", "small", "large"];
SnapPassed.tags = [...icons.BaseSnapPassed.tags, "dynamic", "small", "large"];
SnapFailed.tags = [...icons.BaseSnapFailed.tags, "dynamic", "small", "large"];
SnapPending.tags = [...icons.BaseSnapFailed.tags, "dynamic", "small", "large"];

const LinkedInLogoSmall = () => (
  <icons.LinkedInLogo height="18px" width="18px" />
);
LinkedInLogoSmall.tags = ["linkedin", "logo", "small"];

const CheckGreenXl = () => <icons.CheckGreenBig height="74px" width="74px" />;
CheckGreenXl.tags = ["check", "xl", "approve", "green"];

const PDFPreviewIconGray = styled(icons.PDFPreviewIcon)`
  path {
    fill: ${COLORS.gray2};
  }
`;

const PDFPreviewIconBlue = styled(icons.PDFPreviewIcon)`
  path {
    fill: ${COLORS.lightBlue};
  }
`;

const LogoIconSmall = styled(icons.LogoIcon)`
  height: 10px;
  width: 51px;
`;

const CosignShieldIcon = ({ width = 22, height = 22, fill = "currentColor", fillWhole = "none", ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    xmlSpace="preserve"
    style={{
      enableBackground: "new 0 0 612 792",
    }}
    viewBox="0 0 612 792"
    width={width}
    height={height}
    {...props}
  >
    <path
      d="M304.82 657.58c-67.36.02-134.14.19-200.91-.06-11.35-.04-21.28-4.26-29.63-12.93-3.95-4.1-7.87-7.96-10.07-13.07-3.37-7.81-4.82-15.7-3.31-24.62 1.72-10.13 5.01-19.73 7.73-29.48 2.3-8.24 6.08-16.15 9.88-23.87a437.67 437.67 0 0 1 16.03-29.71c3.21-5.43 7.4-10.3 11.34-15.28 10.18-12.87 22.2-24.87 26.95-40.9 5.04-17.02 6.16-34.64 1.48-52.31-2.81-10.6-9.05-19.8-11.93-30.13-3.34-11.96-8.79-23.42-9.19-36.22-.23-7.54-1.99-15.04-2.17-22.58-.53-22.6 1.35-44.83 8.55-66.59 4.71-14.23 10.97-27.53 18.2-40.53 7.65-13.75 17.37-26.12 28.28-37.13 8-8.08 17.02-15.45 26.59-22.08 9.22-6.38 18.81-11.75 28.84-16.55 11.89-5.69 24.24-9.89 37.09-12.91 15.14-3.56 30.57-6.07 45.91-5.43 18.96.8 37.85 2.81 56.37 8.75 16.36 5.25 31.77 12.33 46.05 21.19 10.63 6.6 20.85 14.42 29.86 23.79 9.61 10 18.78 20.09 26.69 31.52 8.08 11.68 14.39 24.2 19.66 37.23 6.73 16.62 10.38 34.02 12.19 51.94 1.76 17.43.71 34.45-2.24 51.69-2.54 14.88-6.6 28.96-12.75 42.74-6.24 13.97-11.66 28.5-12.2 44.05-.33 9.46 1.6 18.79 4.03 28.03 3.57 13.62 11.22 24.66 19.98 35.28 7.55 9.17 13.92 19.18 20.35 29.2 6.9 10.74 12.64 22.03 17.25 33.64 6.69 16.84 14.48 33.86 13.7 52.56-.62 14.92-8.95 25.82-21.42 33.76-10.13 6.45-21.24 7.13-32.83 7.09-61.24-.19-122.5-.08-184.35-.08z"
      style={{
        fill: fill,
      }}
    />
  </svg>
);
CosignShieldIcon.tags = ["cosign", "shield", "security", "protection"];

const CosignCheckIcon = ({ width = 12, height = 10, stroke = "#D4AF37", ...props }) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 12 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M1 5L4.5 8.5L11 1.5"
      stroke={stroke}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
CosignCheckIcon.tags = ["cosign", "check", "checkmark", "gold", "verified"];

const CosignHouseIcon = ({ width = 20, height = 20, fillMain = '#0283c5', fillHole = '#656a7e', ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    xmlSpace="preserve"
    style={{
      enableBackground: "new 0 0 612 792",
    }}
    viewBox="0 0 612 792"
    width={width}
    height={height}
    {...props}
  >
    {/* <path
      d="M330.83 692H27V100h558v592H330.83z"
      style={{
        fill: "#fff",
      }}
    /> */}
    <path
      d="M304.82 657.58c-67.36.02-134.14.19-200.91-.06-11.35-.04-21.28-4.26-29.63-12.93-3.95-4.1-7.87-7.96-10.07-13.07-3.37-7.81-4.82-15.7-3.31-24.62 1.72-10.13 5.01-19.73 7.73-29.48 2.3-8.24 6.08-16.15 9.88-23.87a437.67 437.67 0 0 1 16.03-29.71c3.21-5.43 7.4-10.3 11.34-15.28 10.18-12.87 22.2-24.87 26.95-40.9 5.04-17.02 6.16-34.64 1.48-52.31-2.81-10.6-9.05-19.8-11.93-30.13-3.34-11.96-8.79-23.42-9.19-36.22-.23-7.54-1.99-15.04-2.17-22.58-.53-22.6 1.35-44.83 8.55-66.59 4.71-14.23 10.97-27.53 18.2-40.53 7.65-13.75 17.37-26.12 28.28-37.13 8-8.08 17.02-15.45 26.59-22.08 9.22-6.38 18.81-11.75 28.84-16.55 11.89-5.69 24.24-9.89 37.09-12.91 15.14-3.56 30.57-6.07 45.91-5.43 18.96.8 37.85 2.81 56.37 8.75 16.36 5.25 31.77 12.33 46.05 21.19 10.63 6.6 20.85 14.42 29.86 23.79 9.61 10 18.78 20.09 26.69 31.52 8.08 11.68 14.39 24.2 19.66 37.23 6.73 16.62 10.38 34.02 12.19 51.94 1.76 17.43.71 34.45-2.24 51.69-2.54 14.88-6.6 28.96-12.75 42.74-6.24 13.97-11.66 28.5-12.2 44.05-.33 9.46 1.6 18.79 4.03 28.03 3.57 13.62 11.22 24.66 19.98 35.28 7.55 9.17 13.92 19.18 20.35 29.2 6.9 10.74 12.64 22.03 17.25 33.64 6.69 16.84 14.48 33.86 13.7 52.56-.62 14.92-8.95 25.82-21.42 33.76-10.13 6.45-21.24 7.13-32.83 7.09-61.24-.19-122.5-.08-184.35-.08z"
      style={{
        fill: fillMain,
      }}
    />
  </svg>
);
CosignHouseIcon.tags = ["cosign", "house", "home", "property"];

const CosignPersonIcon = ({ width = 106, height = 26, iconFill = '#1A5FF4', textFill = '#3A3A3A', ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={width}
    height={height}
    viewBox="0 0 106 26"
    preserveAspectRatio="xMidYMid meet"
    fill="none"
    {...props}
  >
    <g clipPath="url(#cosign-person-clip)">
      <path
        fill={iconFill}
        fillRule="evenodd"
        clipRule="evenodd"
        d="M16.5241 15.5545C15.7292 14.6371 15.6001 13.2045 16.1276 12.1114C16.5743 11.1857 16.8247 10.1475 16.8247 9.05085C16.8247 5.15678 13.668 2 9.77388 2C5.87983 2 2.72306 5.15678 2.72306 9.05085C2.72306 10.1228 2.96226 11.1388 3.3902 12.0486C3.90685 13.1469 3.76364 14.5781 2.95989 15.4876C1.96286 16.616 1.24419 17.996 0.911416 19.5202C0.682288 20.5697 1.57494 21.4506 2.64916 21.4506H16.7764C17.8506 21.4506 18.7433 20.5698 18.5142 19.5202C18.1879 18.0259 17.4908 16.6701 16.5241 15.5545Z"
      />
    </g>
    <path
      fill={textFill}
      d="M34.4603 21.013C32.5639 21.013 30.9106 20.6402 29.5004 19.8946C28.0903 19.1328 26.9962 18.0549 26.2182 16.6609C25.4563 15.2507 25.0754 13.6136 25.0754 11.7496C25.0754 9.8694 25.4401 8.2323 26.1695 6.83834C26.8989 5.44438 27.9282 4.3746 29.2573 3.62899C30.6026 2.86717 32.183 2.48627 33.9984 2.48627C35.4085 2.48627 36.6809 2.75371 37.8155 3.2886C38.9502 3.80729 39.8822 4.54479 40.6116 5.50111C41.341 6.45744 41.8029 7.58395 41.9974 8.88066L38.2046 9.82888C38.0587 9.05085 37.7993 8.37818 37.4265 7.81087C37.0537 7.24356 36.5756 6.80592 35.9921 6.49796C35.4085 6.18999 34.744 6.03601 33.9984 6.03601C33.0421 6.03601 32.1992 6.27103 31.4698 6.74109C30.7566 7.19494 30.2055 7.8595 29.8165 8.73478C29.4275 9.59385 29.233 10.5988 29.233 11.7496C29.233 12.9005 29.4518 13.9054 29.8894 14.7645C30.3271 15.6235 30.9349 16.2881 31.7129 16.7582C32.5072 17.212 33.4392 17.4389 34.509 17.4389C35.3842 17.4389 36.1785 17.2606 36.8916 16.904C37.6048 16.5312 38.1883 16.0126 38.6422 15.348C39.1122 14.6672 39.4121 13.873 39.5418 12.9653L43.4319 13.5002C43.2536 15.0238 42.7673 16.3529 41.9731 17.4876C41.1789 18.6222 40.1334 19.4975 38.8367 20.1134C37.5562 20.7131 36.0974 21.013 34.4603 21.013ZM51.3272 20.8914C49.9495 20.8914 48.7338 20.6159 47.6802 20.0648C46.6429 19.5137 45.8405 18.7356 45.2732 17.7307C44.7059 16.7095 44.4223 15.5263 44.4223 14.181C44.4223 12.8356 44.7059 11.6605 45.2732 10.6555C45.8405 9.63437 46.6429 8.84824 47.6802 8.29714C48.7338 7.74604 49.9495 7.47049 51.3272 7.47049C52.705 7.47049 53.9125 7.74604 54.9499 8.29714C56.0034 8.84824 56.8139 9.63437 57.3812 10.6555C57.9647 11.6605 58.2565 12.8356 58.2565 14.181C58.2565 15.5263 57.9647 16.7095 57.3812 17.7307C56.8139 18.7356 56.0034 19.5137 54.9499 20.0648C53.9125 20.6159 52.705 20.8914 51.3272 20.8914ZM51.3272 17.8036C51.9918 17.8036 52.5672 17.6578 53.0534 17.366C53.5397 17.0742 53.9125 16.6609 54.1718 16.126C54.4474 15.5749 54.5852 14.9266 54.5852 14.181C54.5852 13.4353 54.4474 12.7951 54.1718 12.2602C53.9125 11.7091 53.5397 11.2877 53.0534 10.9959C52.5672 10.7042 51.9918 10.5583 51.3272 10.5583C50.6626 10.5583 50.0872 10.7042 49.601 10.9959C49.1147 11.2877 48.7338 11.7091 48.4583 12.2602C48.1989 12.7951 48.0692 13.4353 48.0692 14.181C48.0692 14.9266 48.1989 15.5749 48.4583 16.126C48.7338 16.6609 49.1147 17.0742 49.601 17.366C50.0872 17.6578 50.6626 17.8036 51.3272 17.8036ZM65.3379 20.8428C64.2032 20.8428 63.1821 20.6807 62.2744 20.3565C61.3829 20.0161 60.6616 19.5218 60.1105 18.8734C59.5756 18.2251 59.2839 17.447 59.2353 16.5393L62.4689 16.1746C62.5175 16.6123 62.6796 17.0013 62.9552 17.3417C63.2307 17.6659 63.5954 17.9171 64.0493 18.0954C64.5031 18.2737 65.0056 18.3628 65.5567 18.3628C66.0105 18.3628 66.432 18.298 66.821 18.1683C67.21 18.0387 67.5098 17.8685 67.7206 17.6578C67.9475 17.4308 68.0609 17.1796 68.0609 16.904C68.0609 16.5312 67.8583 16.2314 67.4531 16.0045C67.0641 15.7613 66.432 15.5668 65.5567 15.4209L63.7089 15.1292C62.3311 14.9185 61.3343 14.5375 60.7184 13.9864C60.1024 13.4191 59.7945 12.6249 59.7945 11.6037C59.7945 10.7771 60.0133 10.0558 60.4509 9.43986C60.9048 8.82393 61.5369 8.35387 62.3473 8.02969C63.1578 7.68931 64.0898 7.51911 65.1434 7.51911C66.1159 7.51911 66.9993 7.6731 67.7935 7.98107C68.5877 8.28904 69.228 8.71857 69.7142 9.26967C70.2167 9.82077 70.5166 10.461 70.6138 11.1904L67.4531 11.6524C67.4045 11.1175 67.1532 10.6961 66.6994 10.3881C66.2456 10.0639 65.6864 9.90182 65.0218 9.90182C64.6328 9.90182 64.2843 9.96665 63.9763 10.0963C63.6684 10.2098 63.4171 10.3719 63.2226 10.5826C63.0443 10.7933 62.9552 11.0202 62.9552 11.2634C62.9552 11.5875 63.1173 11.855 63.4414 12.0657C63.7818 12.2602 64.3248 12.4223 65.0704 12.552L66.9182 12.868C68.4743 13.1598 69.6089 13.6055 70.3221 14.2053C71.0352 14.7888 71.3918 15.5911 71.3918 16.6123C71.3918 17.4551 71.1325 18.2008 70.6138 18.8491C70.1113 19.4813 69.3982 19.9756 68.4743 20.3322C67.5666 20.6726 66.5211 20.8428 65.3379 20.8428ZM72.8787 20.4052V7.95675H76.5743V20.4052H72.8787ZM74.7265 6.25482C74.0782 6.25482 73.5514 6.06032 73.1462 5.67131C72.741 5.28229 72.5384 4.77171 72.5384 4.13957C72.5384 3.71814 72.6275 3.34533 72.8058 3.02116C73.0003 2.69698 73.2597 2.44574 73.5838 2.26745C73.908 2.08915 74.2889 2 74.7265 2C75.3911 2 75.926 2.19451 76.3312 2.58352C76.7364 2.97253 76.939 3.49121 76.939 4.13957C76.939 4.561 76.8418 4.9338 76.6473 5.25798C76.469 5.56595 76.2096 5.80908 75.8693 5.98738C75.5451 6.16568 75.1642 6.25482 74.7265 6.25482ZM84.1166 25.9972C82.8361 25.9972 81.6042 25.7865 80.421 25.3651C79.2539 24.9436 78.33 24.3844 77.6493 23.6874L78.9379 20.9157C79.4889 21.5317 80.2021 22.0179 81.0774 22.3745C81.9689 22.7311 82.8766 22.9094 83.8005 22.9094C84.6433 22.9094 85.3808 22.7473 86.013 22.4232C86.6451 22.1152 87.1314 21.6694 87.4718 21.0859C87.8284 20.5186 88.0067 19.8459 88.0067 19.0679V18.0711H87.9823C87.7068 18.5411 87.3421 18.9464 86.8883 19.2867C86.4344 19.6271 85.9319 19.8946 85.3808 20.0891C84.8297 20.2674 84.2705 20.3565 83.7032 20.3565C82.601 20.3565 81.6366 20.0972 80.81 19.5785C79.9995 19.0598 79.3674 18.3223 78.9135 17.366C78.4597 16.3935 78.2328 15.2751 78.2328 14.0108C78.2328 12.7303 78.4516 11.6119 78.8892 10.6555C79.3431 9.683 79.9671 8.93739 80.7613 8.41871C81.5718 7.88381 82.5119 7.61637 83.5817 7.61637C84.5056 7.61637 85.3727 7.86761 86.1832 8.37008C86.9936 8.85635 87.6014 9.5047 88.0067 10.3151H88.031L88.5902 7.95675H91.7266V19.1895C91.7266 20.5672 91.4105 21.7667 90.7784 22.7879C90.1624 23.809 89.2791 24.5951 88.1282 25.1462C86.9774 25.7136 85.6402 25.9972 84.1166 25.9972ZM85.0405 17.3417C85.6564 17.3417 86.1994 17.2039 86.6694 16.9284C87.1395 16.6366 87.5042 16.2395 87.7635 15.737C88.0229 15.2345 88.1525 14.6591 88.1525 14.0108C88.1525 13.3624 88.0229 12.7789 87.7635 12.2602C87.5042 11.7415 87.1395 11.3363 86.6694 11.0445C86.1994 10.7528 85.6564 10.6069 85.0405 10.6069C84.4407 10.6069 83.8977 10.7528 83.4115 11.0445C82.9414 11.3201 82.5767 11.7172 82.3174 12.2359C82.058 12.7546 81.9284 13.3462 81.9284 14.0108C81.9284 14.6591 82.058 15.2345 82.3174 15.737C82.5767 16.2395 82.9414 16.6366 83.4115 16.9284C83.8977 17.2039 84.4407 17.3417 85.0405 17.3417ZM101.821 7.59206C102.68 7.59206 103.409 7.76225 104.009 8.10263C104.625 8.42681 105.095 8.91308 105.419 9.56143C105.759 10.1936 105.929 10.9473 105.929 11.8226V20.4052H102.21V12.9653C102.21 12.2521 102.023 11.6929 101.65 11.2877C101.278 10.8825 100.767 10.6798 100.119 10.6798C99.5837 10.6798 99.1056 10.8014 98.6841 11.0445C98.2789 11.2715 97.9628 11.6037 97.7359 12.0414C97.509 12.4628 97.3955 12.9572 97.3955 13.5245V20.4052H93.6756V7.95675H96.812L97.3712 10.2179H97.3955C97.8494 9.37503 98.4572 8.72667 99.219 8.27283C99.9808 7.81898 100.848 7.59206 101.821 7.59206Z"
    />
    <defs>
      <clipPath id="cosign-person-clip">
        <rect width="19.4505" height="19.4506" fill="white" transform="translate(0 2)" />
      </clipPath>
    </defs>
  </svg>
);
CosignPersonIcon.tags = ["cosign", "person", "user", "profile"];

const CosignCheckCircleIcon = ({ width = 20, height = 20, stroke = "#16A34A", ...props }) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <circle cx="10" cy="10" r="8" stroke={stroke} strokeWidth="1.5" />
    <path
      d="M6.5 10L9 12.5L14 7.5"
      stroke={stroke}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
CosignCheckCircleIcon.tags = ["cosign", "check", "circle", "success", "green"];

const ExternalLinkIcon = ({ width = 16, height = 16, stroke = "currentColor", ...props }) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M6 3.33333H4C3.44772 3.33333 3 3.78105 3 4.33333V12C3 12.5523 3.44772 13 4 13H11.6667C12.219 13 12.6667 12.5523 12.6667 12V10M10 3.33333H12.6667M12.6667 3.33333V6M12.6667 3.33333L6.5 9.5"
      stroke={stroke}
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
ExternalLinkIcon.tags = ["external", "link", "arrow", "open"];

const SparkleIcon = ({ width = 14, height = 14, fill = "#F59E0B", ...props }) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M7 0L8.12 4.88L13 6L8.12 7.12L7 12L5.88 7.12L1 6L5.88 4.88L7 0Z"
      fill={fill}
    />
  </svg>
);
SparkleIcon.tags = ["sparkle", "star", "available", "new", "highlight"];

export {
  BellIcon,
  ProfileIcon,
  SupportIcon,
  SettingsIcon,
  ArrowUpIcon,
  EmployeesIcon,
  MyPropertyBoldIcon,
  MyPropertyIcon,
  DealsIcon,
  LeadsIcon,
  ApplicantsTenantsIcon,
  ApplicantInvite,
  StarOutlineIcon,
  DropdownIcon,
  DeleteIcon,
  DeleteSmallIcon,
  DeleteSmallNoCircleIcon,
  ChevronUpIcon,
  ChevronUpGreenIcon,
  ChevronBoldUpIcon,
  ProfileIconWithChevron,
  PdfDownloadIcon,
  ImportantIcon,
  IntegrationsMenuItemIcon,
  SignOutIcon,
  PersonalInfoIcon,
  ShowPasswordIcon,
  ShowPasswordActionIcon,
  HidePasswordIcon,
  HidePasswordActionIcon,
  DropdownButtonChevronUpIcon,
  SelectArrowCompleteIcon,
  ApplicationsIcon,
  VerifiedIncomeAssetsIcon,
  NotVerifiedIncomeAssetsIcon,
  DocumentsIcon,
  LeaseEyeIcon,
  ArrowLeftUpIcon,
  ArrowRightUpIcon,
  ZoomPlusIcon,
  ZoomMinusIcon,
  CrossRemoveIcon,
  DollarSignIcon,
  NoteIcon,
  QRCodeGreenHoverIcon,
  DocumentsEmptyGreenHoverIcon,
  OrangeExclamationSmall,
  BulbGreenBig,
  BulbOrangeBig,
  BulbPurpleBig,
  BulbRedBig,
  DownloadPdfDoc,
  ExportIconHover,
  AddAttachmentPlus,
  RefreshIconSmall,
  RefreshRotateIconSmall,
  ErrorXWarningGray,
  DownloadPdfDocGray,
  LynkSuccess,
  LynkSkipped,
  SnapPassed,
  SnapFailed,
  SnapPending,
  LinkedInLogoSmall,
  CheckGreenXl,
  PDFPreviewIconGray,
  PDFPreviewIconBlue,
  LogoIconSmall,
  ChevronUpWhiteBoldIcon,
  CosignShieldIcon,
  CosignCheckIcon,
  CosignHouseIcon,
  CosignPersonIcon,
  CosignCheckCircleIcon,
  ExternalLinkIcon,
  SparkleIcon,
};
